// Unpublished Work © 2020-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import OnLinkDialogContentBar from 'Ui/components/common/onlink-dialog/onlink-dialog-content-bar';
import OnlinkButton from 'Ui/components/common/onlink-button';
import {joinClassNames} from 'Utils/html-utils';
import {IconLoading} from '@deere/icons';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {Stack} from '@mui/material';

const ICON_LOADING_STYLE = {
    style: {
        height: '16px',
        width: '16px'
    }
};

function SaveContentBar(props) {
    const {
        className,
        disabled,
        loading,
        onCancelClick,
        onSaveClick,
        readOnly,
        translations,
        children
    } = props;

    const {addToast} = useToast();
    const contentBarClassName = React.useMemo(() => joinClassNames('settings-actions', className), [className]);
    const loadingIcon = React.useMemo(() => loading ? <IconLoading {...ICON_LOADING_STYLE}/> : null, [loading]);

    return (
        <OnLinkDialogContentBar className={contentBarClassName}>
            <Stack
                alignItems='center'
                direction='row'
                justifyContent={children ? 'space-between' : 'end'}
                sx={{
                    width: '100%'
                }}
            >
                {children}
                <Stack direction='row'>
                    <OnlinkButton onClick={onCancelClick}>
                        {readOnly ? translations.CLOSE : translations.CANCEL}
                    </OnlinkButton>
                    {
                        !readOnly &&
                        <OnlinkButton
                            className='primary'
                            disabled={disabled}
                            onClick={async (event) => {
                                try {
                                    await onSaveClick(event);
                                } catch {
                                    addToast({
                                        message: translations.ERROR_OCCURRED_TITLE,
                                        type: TOAST_TYPE.ERROR
                                    });
                                }
                            }}
                            startIcon={loadingIcon}
                            variant='primary'
                        >
                            {translations.save}
                        </OnlinkButton>
                    }
                </Stack>
            </Stack>
        </OnLinkDialogContentBar>
    );
}

SaveContentBar.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    onCancelClick: PropTypes.func,
    onSaveClick: PropTypes.func,
    readOnly: PropTypes.bool,
    translations: PropTypes.translations
};

export default SaveContentBar;
